import isMeetingCancellation from './isMeetingCancellation';
import isMeetingRequest from './isMeetingRequest';
import isMeetingResponse from './isMeetingResponse';
import MeetingMessageActionEntrySource from '../store/schema/MeetingMessageActionEntrySource';
import { logUsage } from 'owa-analytics-actions';
import MeetingMessage from 'owa-service/lib/contract/MeetingMessage';
import MeetingRequestMessageType from 'owa-service/lib/contract/MeetingRequestMessageType';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { getUserConfiguration } from 'owa-session-store';
import { markSessionStart, markSessionEnd } from './sessionDurationTelemetryHelpers';

export type ActionType =
    | 'ViewOpened' // A view from the ViewType list is opened
    | 'ViewClosed' // A view from the ViewType list is closed
    | 'Accept' // A calendar event is sucessfully accepted
    | 'Tentative' // A calendar event is sucessfully responded with Tentative
    | 'Decline' // A calendar event is sucessfully declined
    | 'PNTDeclineStarted' // The decline and propose new time workflow is started
    | 'PNTTentativeStarted' // The tentatively accept and propose new time workflow is started
    | 'ShowConflicts' // The show conflicts button is clicked
    | 'HideConflicts' // The hide conflicts button is clicked
    | 'RemoveFromCalendar' // Remove from calendar is clicked for a canceled invitation
    | 'AddToMyCalendar' // The Add group event to my calendar button is clicked (Group calendar)
    | 'NoResponseClicked' // The no response required button is clicked (informational update, deletes the meeting message)
    | 'ReviewPNT'; // The edit event button is clicked to review Proposed new time(s)

type ViewType =
    | 'MeetingInvite'
    | 'MeetingInformationalUpdate'
    | 'MeetingUpdate'
    | 'MeetingCancelation'
    | 'MeetingResponse';

type AttendeeRole = 'Organizer' | 'Delegate' | 'Required' | 'Optional';

type EventType = 'Instance' | 'Series' | 'Single';

export default function logMeetingMessageAction(
    interactionType: ActionType,
    item: MeetingMessage,
    entrySource: MeetingMessageActionEntrySource,
    propertyNames?: string[]
) {
    // Mark the session end if it hasn't ended yet
    let timeInMessage = 0;
    if (interactionType == 'ViewOpened') {
        markSessionStart(item.ItemId.Id, entrySource);
    } else {
        timeInMessage = markSessionEnd(item.ItemId.Id, entrySource);
    }

    const readingSetting: ReactListViewType = getUserConfiguration().UserOptions
        .GlobalListViewTypeReact;

    const eventType: EventType = item.RecurrenceId
        ? 'Instance'
        : (item as MeetingRequestMessageType).Recurrence // Series information is only on Requests
        ? 'Series'
        : 'Single';

    // TODO VSO: 46665 Update logging to reflect HideAttendees setting
    const attendeeCount: number =
        (item.ToRecipients || []).length +
        (item.CcRecipients || []).length +
        (item.BccRecipients || []).length;

    const propertyNamesList: string = (propertyNames || []).join(';');

    const viewType = getMeetingMessageViewType(item);
    const attendeeType = getMeetingMessageAttendeeRole(item);
    const readingPanePosition = getUserConfiguration().UserOptions.GlobalReadingPanePositionReact;
    logUsage('meetingMessageAction', {
        interactionType,
        viewType,
        entrySource,
        attendeeType,
        readingSetting,
        eventType,
        attendeeCount,
        timeInMessage,
        propertyNamesList,
        readingPanePosition,
    });
}

function getMeetingMessageViewType(item: MeetingMessage): ViewType {
    if (isMeetingRequest(item.ItemClass)) {
        const request = item as MeetingRequestMessageType;
        switch (request.MeetingRequestType) {
            case 'NewMeetingRequest':
                return 'MeetingInvite';
            case 'FullUpdate':
                return 'MeetingUpdate';
            case 'InformationalUpdate':
                return 'MeetingInformationalUpdate';
        }
    }
    if (isMeetingCancellation(item.ItemClass)) {
        return 'MeetingCancelation';
    }
    if (isMeetingResponse(item.ItemClass)) {
        return 'MeetingResponse';
    }
    return null;
}

function getMeetingMessageAttendeeRole(item: MeetingMessage): AttendeeRole {
    if (item.IsDelegated) {
        return 'Delegate';
    }
    if (item.IsOrganizer) {
        return 'Organizer';
    }

    if (
        item.CcRecipients &&
        item.CcRecipients.some(
            cc =>
                cc.EmailAddress.toLowerCase() ===
                getUserConfiguration().SessionSettings.UserEmailAddress.toLowerCase()
        )
    ) {
        return 'Optional';
    }
    return 'Required';
}
